'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import CloseWhite1414 from '@svg/sharing-trips/close_white_14_14.svg';
import usePreventBodyScroll from '@/app/hooks/usePreventBodyScroll';

export default function SharingTripImageModal({
  selectedImageIndex: initialIndex,
  poiImages,
}: {
  selectedImageIndex: number;
  poiImages: string[];
}) {
  const router = useRouter();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const carouselRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef({
    isDragging: false,
    startX: 0,
    currentTranslate: 0,
    prevTranslate: 0,
    animationFrame: 0,
  });

  const animation = useRef({
    imageWidth: 0,
    containerWidth: 0,
  });

  usePreventBodyScroll();

  useEffect(() => {
    if (carouselRef.current) {
      animation.current.containerWidth = carouselRef.current.offsetWidth;
      animation.current.imageWidth = carouselRef.current.offsetWidth;
      setPositionByIndex(initialIndex);
    }
  }, []);

  const setPositionByIndex = (index: number) => {
    dragRef.current.currentTranslate = -(index * animation.current.imageWidth);
    dragRef.current.prevTranslate = dragRef.current.currentTranslate;
    setPosition();
  };

  const setPosition = () => {
    if (!carouselRef.current) return;
    carouselRef.current.style.transform = `translateX(${dragRef.current.currentTranslate}px)`;
  };

  const handleSlideChange = (newIndex: number) => {
    if (newIndex >= 0 && newIndex < poiImages.length) {
      setCurrentIndex(newIndex);
      setPositionByIndex(newIndex);
    }
  };

  const animation_frame = () => {
    setPosition();
    if (dragRef.current.isDragging) {
      dragRef.current.animationFrame = requestAnimationFrame(animation_frame);
    }
  };

  const touchStart = (e: React.TouchEvent | React.MouseEvent) => {
    dragRef.current.isDragging = true;
    dragRef.current.startX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    dragRef.current.animationFrame = requestAnimationFrame(animation_frame);
    if (carouselRef.current) {
      carouselRef.current.style.transition = 'none';
    }
  };

  const touchMove = (e: React.TouchEvent | React.MouseEvent) => {
    if (!dragRef.current.isDragging) return;

    const currentPosition = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const currentDistance = currentPosition - dragRef.current.startX;
    dragRef.current.currentTranslate = dragRef.current.prevTranslate + currentDistance;
  };

  const touchEnd = () => {
    cancelAnimationFrame(dragRef.current.animationFrame);
    dragRef.current.isDragging = false;

    if (carouselRef.current) {
      carouselRef.current.style.transition = 'transform 0.3s ease-out';
    }

    const movedBy = dragRef.current.currentTranslate - dragRef.current.prevTranslate;
    const threshold = animation.current.imageWidth * 0.2;

    if (Math.abs(movedBy) > threshold) {
      if (movedBy > 0 && currentIndex > 0) {
        handleSlideChange(currentIndex - 1);
      } else if (movedBy < 0 && currentIndex < poiImages.length - 1) {
        handleSlideChange(currentIndex + 1);
      } else {
        handleSlideChange(currentIndex);
      }
    } else {
      handleSlideChange(currentIndex);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
          handleSlideChange(currentIndex - 1);
          break;
        case 'ArrowRight':
          handleSlideChange(currentIndex + 1);
          break;
        case 'Escape':
          router.back();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      cancelAnimationFrame(dragRef.current.animationFrame);
    };
  }, [currentIndex]);

  return (
    <div className='fixed w-full max-w-480pxr h-dvh top-0 z-20 bg-[#0A0A0A]'>
      <div className='absolute z-10 w-full top-0 py-16pxr px-20pxr flex justify-between items-center'>
        <span className='w-24pxr h-24pxr'></span>
        <span className='body-16-md text-white'>
          {currentIndex + 1} / {poiImages.length}
        </span>
        <button className='w-24pxr h-24pxr flex items-center justify-center' onClick={() => router.back()}>
          <CloseWhite1414 />
        </button>
      </div>

      <div className='w-full h-full overflow-hidden pt-56pxr'>
        <div
          ref={carouselRef}
          className='w-full h-full flex cursor-grab active:cursor-grabbing select-none'
          onTouchStart={touchStart}
          onTouchMove={touchMove}
          onTouchEnd={touchEnd}
          onMouseDown={touchStart}
          onMouseMove={touchMove}
          onMouseUp={touchEnd}
          onMouseLeave={touchEnd}
        >
          {poiImages.map((image, index) => (
            <div key={image} className='w-full h-full flex-shrink-0'>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={image}
                alt={`이미지 ${index + 1}`}
                className='w-full h-full object-contain'
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
